import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormikDatePicker, FormikDecimalTextField } from "@shared";
import { startOfToday } from "date-fns";
import { FormikProps } from "formik";

import { CalculationParameterEntryStatus, CalculationParameterResponse, CalculationParameterUnit } from "@/api";
import {
  DecimalScale,
  ParameterEntryFormValues,
  ParameterFormValues,
  PercentageMax,
} from "@/pages/system-settings/tabs/calculation-parameters/CalculationParametersSettings.types";
import {
  isEffectiveFromEditable,
  isPersistedFutureEntry,
} from "@/pages/system-settings/tabs/calculation-parameters/CalculationParametersSettings.utils";
import { useTranslation } from "@/translations";

interface Props {
  parameter: CalculationParameterResponse;
  formik: FormikProps<ParameterFormValues>;
  isSaving: boolean;
  onAdd: () => void;
  onRemoveNew: (index: number) => void;
  onDelete: (entry: ParameterEntryFormValues) => void;
}

// A medium IconButton (40px) plus the 16px inset that keeps it off the table border. Stated because
// tableLayout is fixed, so the column would otherwise squeeze the button.
const ActionColumnWidth = 56;
const StatusColumnWidth = 128;
const InputHeight = 40;

export function ParameterEntriesTable({ parameter, formik, isSaving, onAdd, onRemoveNew, onDelete }: Props) {
  const { t } = useTranslation();
  const entries = formik.values.entries;
  const isPercentage = parameter.unit === CalculationParameterUnit.Percentage;
  const unitSymbol = isPercentage ? "%" : "€";
  const today = startOfToday();

  const renderStatus = (status: CalculationParameterEntryStatus | null) => {
    if (status === CalculationParameterEntryStatus.Active) {
      return <Chip label={t("settings.parameters.active")} size="small" color="primary" />;
    }
    if (status === CalculationParameterEntryStatus.Future) {
      return <Chip label={t("settings.parameters.future")} size="small" variant="outlined" />;
    }
    return null;
  };

  const renderAction = (entry: ParameterEntryFormValues, index: number) => {
    if (entry.id === null) {
      return (
        <IconButton
          onClick={() => onRemoveNew(index)}
          size="medium"
          aria-label={t("settings.parameters.removeNewValue")}
        >
          <DeleteIcon />
        </IconButton>
      );
    }

    if (!isPersistedFutureEntry(entry)) {
      return null;
    }

    const disabled = formik.dirty || isSaving;
    const button = (
      <IconButton
        onClick={() => onDelete(entry)}
        size="medium"
        disabled={disabled}
        aria-label={t("settings.parameters.deleteValue")}
      >
        <DeleteIcon />
      </IconButton>
    );

    // A disabled button fires no events, so the tooltip explaining why sits on a wrapping span.
    return disabled ? (
      <Tooltip title={t("settings.parameters.deleteDisabledWhileDirty")}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <Stack flex={1} minHeight={0} gap={1.5} alignItems="flex-start">
      <Typography variant="subtitle2" color="text.secondary">
        {t("settings.parameters.values")}
      </Typography>

      <TableContainer
        sx={{
          width: "100%",
          minHeight: 0,
          borderRadius: 1.5,
          overflowY: "auto",
          border: (theme) => `1px solid ${theme.palette.fsw["grey-50"]}`,
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "fixed",
            "& thead th": {
              fontWeight: 700,
              backgroundColor: (theme) => theme.palette.fsw["grey-25"],
              py: 1.25,
            },
            "& tbody td": {
              verticalAlign: "top",
              py: 1.25,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("settings.parameters.effectiveFrom")}</TableCell>
              <TableCell>{t("settings.parameters.value")}</TableCell>
              <TableCell width={StatusColumnWidth} />
              <TableCell width={ActionColumnWidth} />
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    {t("settings.parameters.noEntries")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry, index) => (
                <TableRow key={entry.rowKey}>
                  <TableCell>
                    <FormikDatePicker
                      formik={formik}
                      path={`entries.${index}.effectiveFrom`}
                      required
                      disabled={!isEffectiveFromEditable(entry)}
                      minDate={isEffectiveFromEditable(entry) ? today : undefined}
                      textFieldProps={{ inputProps: { "aria-label": t("settings.parameters.effectiveFrom") } }}
                    />
                  </TableCell>
                  <TableCell>
                    <FormikDecimalTextField
                      formik={formik}
                      path={`entries.${index}.value`}
                      required
                      decimalScale={DecimalScale}
                      minValue={0}
                      maxValue={isPercentage ? PercentageMax : undefined}
                      slotProps={{
                        input: {
                          sx: { pr: 0 },
                          endAdornment: <InputAdornment position="start">{unitSymbol}</InputAdornment>,
                          inputProps: { "aria-label": t("settings.parameters.value") },
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" height={InputHeight}>
                      {renderStatus(entry.status)}
                    </Box>
                  </TableCell>
                  {/* No left padding: the status cell's own padding is the gap to the icon, so the two do
                      not stack into a wide gutter. */}
                  <TableCell sx={{ pl: 0, pr: 2 }}>{renderAction(entry, index)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Button variant="outlined" startIcon={<AddRoundedIcon />} onClick={onAdd} disabled={isSaving}>
        {t("settings.parameters.addValue")}
      </Button>
    </Stack>
  );
}
